import React, { useState } from "react";
import { Table, Pagination, CircularProgress, SelectToolbar, Typography, Button } from "@ann/web-components";
import Filters from "../Filters";
import Icon from "../../../../../../../../../elements/icon/Icon";
import { DeactivateReactivateUserModal } from "../../../../components";
import { deactivateUser, activateUser } from "../../../../../../../../../services/users";
import { useToast } from "../../../../../../../../../components/toast";
import { TableWrapper, TableContainer, PaginationWrapper, LoadingContainer, NoDataWrapper, NoDataTitle, NoDataDescription, SelectToolbarWrapper, ActionContent, ActionWithLabel, ActionLabel } from "./UserTableSection.styled";

const UserTableSection = ({
  selectedItemsCount = 0,
  selectedUserUuids = [],
  onClearSelection,
  tableData,
  headers,
  onSelectionChange,
  currentPage,
  itemsPerPage,
  onItemsPerPageChange,
  onPageChange,
  totalItems,
  totalPages,
  loading,
  onSortChange,
  onOrderChange,
  selectedSortOption,
  orderDirection,
  onSearchChange,
  onRefresh, // Function to refresh users list after deactivate/activate
}) => {
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [tableKey, setTableKey] = useState(0); // Key to force table re-render and clear selection
  const showEmptyState = !loading && tableData.length === 0;
  const toast = useToast();

  const handleDeactivateClick = () => {
    setIsDeactivateModalOpen(true);
  };

  const handleReactivateClick = () => {
    setIsReactivateModalOpen(true);
  };

  // Obtener el usuario seleccionado cuando solo hay uno
  const selectedUser = selectedItemsCount === 1 && tableData.length > 0
    ? tableData.find(user => selectedUserUuids.includes(user.id))
    : null;

  // Get selected users' status to determine which action buttons to show
  const selectedUsers = tableData.filter(user => selectedUserUuids.includes(user.id));
  
  // Helper function to determine if a user is truly "active"
  // A user is active if BOTH status === "active" AND isActive === true (if field exists)
  const isUserActive = (user) => {
    const statusActive = user.rawStatus?.toLowerCase() === 'active';
    // If isActive field exists, consider it; otherwise, rely only on status
    if (user.isActive !== undefined) {
      return statusActive && user.isActive === true;
    }
    return statusActive;
  };

  // Helper function to determine if a user is NOT active
  // A user is NOT active if status !== "active" OR isActive === false
  const isUserInactive = (user) => {
    const statusInactive = user.rawStatus?.toLowerCase() !== 'active';
    // If isActive field exists, consider it
    if (user.isActive !== undefined) {
      return statusInactive || user.isActive === false;
    }
    return statusInactive;
  };
  
  // Show Deactivate button only if ALL selected users are "active" (status + isActive)
  const showDeactivateButton = selectedUsers.length > 0 && selectedUsers.every(user => isUserActive(user));
  
  // Show Reactivate button only if ALL selected users are NOT "active" (status or isActive)
  const showReactivateButton = selectedUsers.length > 0 && selectedUsers.every(user => isUserInactive(user));

  const handleDeactivateConfirm = async () => {
    setIsDeactivating(true);
    try {
      // Call deactivateUser service with selected user UUIDs
      // The service handles both single and multiple users with Promise.all
      const response = await deactivateUser(selectedUserUuids);
      
      // Close modal
      setIsDeactivateModalOpen(false);
      
      // Clear selection
      if (onClearSelection) {
        onClearSelection();
      }
      
      // Refresh users list to reflect changes
      if (onRefresh) {
        onRefresh();
      }
      
      // Show success toast
      const isSingleUser = selectedUserUuids.length === 1;
      if (isSingleUser && selectedUser) {
        // Single user: "{fullName} deactivated successfully"
        const message = Array.isArray(response?.msgCode) && response.msgCode.length > 0
          ? response.msgCode[0]?.description || "User has been deactivated"
          : response?.message || "User has been deactivated";
        
        toast.success({
          title: `${selectedUser.name} deactivated successfully`,
          message: message
        });
      } else {
        // Multiple users: Generic message
        const message = Array.isArray(response?.msgCode) && response.msgCode.length > 0
          ? response.msgCode[0]?.description || "Users have been deactivated"
          : response?.message || "Users have been deactivated";
        
        toast.success({
          title: `${selectedUserUuids.length} users deactivated successfully`,
          message: message
        });
      }
    } catch (error) {
      console.error('Error deactivating users:', error);
      
      // Show error toast
      const isSingleUser = selectedUserUuids.length === 1;
      if (isSingleUser && selectedUser) {
        // Single user: "Couldn't deactivate the {fullName}"
        const errorMessage = error.response?.data?.msgCode?.[0]?.description 
          || error.response?.data?.message 
          || error.message 
          || "An error occurred while deactivating the user";
        
        toast.error({
          title: `Couldn't deactivate the ${selectedUser.name}`,
          message: errorMessage
        });
      } else {
        // Multiple users: Generic error message
        const errorMessage = error.response?.data?.msgCode?.[0]?.description 
          || error.response?.data?.message 
          || error.message 
          || "An error occurred while deactivating the users";
        
        toast.error({
          title: "Some users couldn't be deactivated",
          message: errorMessage
        });
      }
      
      // Keep modal open on error so user can retry
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleReactivateConfirm = async () => {
    setIsReactivating(true);
    try {
      // Call activateUser service with selected user UUIDs
      // The service handles both single and multiple users with Promise.all
      const response = await activateUser(selectedUserUuids);
      
      // Close modal
      setIsReactivateModalOpen(false);
      
      // Clear selection
      if (onClearSelection) {
        onClearSelection();
      }
      
      // Refresh users list to reflect changes
      if (onRefresh) {
        onRefresh();
      }
      
      // Show success toast
      const isSingleUser = selectedUserUuids.length === 1;
      if (isSingleUser && selectedUser) {
        // Single user: "{fullName} reactivated successfully"
        const message = Array.isArray(response?.msgCode) && response.msgCode.length > 0
          ? response.msgCode[0]?.description || "User has been reactivated"
          : response?.message || "User has been reactivated";
        
        toast.success({
          title: `${selectedUser.name} reactivated successfully`,
          message: message
        });
      } else {
        // Multiple users: Generic message
        const message = Array.isArray(response?.msgCode) && response.msgCode.length > 0
          ? response.msgCode[0]?.description || "Users have been reactivated"
          : response?.message || "Users have been reactivated";
        
        toast.success({
          title: `${selectedUserUuids.length} users reactivated successfully`,
          message: message
        });
      }
    } catch (error) {
      console.error('Error reactivating users:', error);
      
      // Show error toast
      const isSingleUser = selectedUserUuids.length === 1;
      if (isSingleUser && selectedUser) {
        // Single user: "Couldn't reactivate the {fullName}"
        const errorMessage = error.response?.data?.msgCode?.[0]?.description 
          || error.response?.data?.message 
          || error.message 
          || "An error occurred while reactivating the user";
        
        toast.error({
          title: `Couldn't reactivate the ${selectedUser.name}`,
          message: errorMessage
        });
      } else {
        // Multiple users: Generic error message
        const errorMessage = error.response?.data?.msgCode?.[0]?.description 
          || error.response?.data?.message 
          || error.message 
          || "An error occurred while reactivating the users";
        
        toast.error({
          title: "Some users couldn't be reactivated",
          message: errorMessage
        });
      }
      
      // Keep modal open on error so user can retry
    } finally {
      setIsReactivating(false);
    }
  };

  const handleDeactivateModalClose = () => {
    if (!isDeactivating) {
      setIsDeactivateModalOpen(false);
    }
  };

  const handleReactivateModalClose = () => {
    if (!isReactivating) {
      setIsReactivateModalOpen(false);
    }
  };

  return (
    <TableWrapper>
      <TableContainer>
        {selectedItemsCount >= 1 ? (
          <SelectToolbarWrapper>
            <SelectToolbar 
              items={selectedItemsCount} 
              itemLabel='User'
              onClose={() => {
                // Increment tableKey to force table re-render and clear checkboxes
                setTableKey((prev) => prev + 1);
                // Also call onClearSelection to clear parent state
                if (onClearSelection) {
                  onClearSelection();
                }
              }}
            >
            {showDeactivateButton && (
              <ActionWithLabel>
                <Button
                  type='tertiary'
                  size='small'
                  icon='person_off'
                  onClick={handleDeactivateClick}
                />
                <ActionLabel>
                  <Typography category='link' weight='regular' size='small'>
                    Deactivate
                  </Typography>
                </ActionLabel>
              </ActionWithLabel>
            )}
            {showReactivateButton && (
              <ActionWithLabel>
                <Button
                  type='tertiary'
                  size='small'
                  icon='person_add'
                  onClick={handleReactivateClick}
                />
                <ActionLabel>
                  <Typography category='link' weight='regular' size='small'>
                    Reactivate
                  </Typography>
                </ActionLabel>
              </ActionWithLabel>
            )}
            </SelectToolbar>
          </SelectToolbarWrapper>
        ) : (
          <Filters
            onSortChange={onSortChange}
            onOrderChange={onOrderChange}
            selectedSortOption={selectedSortOption}
            orderDirection={orderDirection}
            onSearchChange={onSearchChange}
            loading={loading}
          />
        )}
        {loading ? (
          <LoadingContainer>
            <CircularProgress size="xl" />
          </LoadingContainer>
        ) : showEmptyState ? (
          <NoDataWrapper>
            <Icon name="noDataAvailable" width="201px" height="195px" />
            <NoDataTitle>No users yet</NoDataTitle>
            <NoDataDescription>
              No users found in this workspace.
            </NoDataDescription>
          </NoDataWrapper>
        ) : (
          <Table
            key={tableKey}
            selectable
            data={tableData}
            headers={headers}
            mode="table"
            showActions={false}
            onSelectionChange={onSelectionChange}
          />
        )}
      </TableContainer>
      {!showEmptyState && (
        <PaginationWrapper>
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={onItemsPerPageChange}
            onPageChange={onPageChange}
            totalItems={totalItems}
            totalPages={totalPages}
            disabled={loading}
          />
        </PaginationWrapper>
      )}
      <DeactivateReactivateUserModal
        isOpen={isDeactivateModalOpen}
        onClose={handleDeactivateModalClose}
        onConfirm={handleDeactivateConfirm}
        selectedUsersCount={selectedItemsCount}
        selectedUser={selectedUser}
        isLoading={isDeactivating}
        action="deactivate"
      />
      <DeactivateReactivateUserModal
        isOpen={isReactivateModalOpen}
        onClose={handleReactivateModalClose}
        onConfirm={handleReactivateConfirm}
        selectedUsersCount={selectedItemsCount}
        selectedUser={selectedUser}
        isLoading={isReactivating}
        action="reactivate"
      />
    </TableWrapper>
  );
};

export default UserTableSection;
